import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { EditLike } from '../earlierVersions.ts';

/**
 * A person works a task: the screens' reads and writes against 147's routes
 * (Arthur's rulings A-#77 to A-#79; Code 1's contract, version 3.1, and
 * the Task object in api/tasks.ts). Every shape here is the contract's. The server groups, permits and resolves: the screen renders the
 * groups it is given, shows a button only where the task's `may` says so, and
 * shows a refusal's `error` as it is.
 */

export * from '../tasks/model.ts';
import type { Department, DueSpec, FollowUp, FollowUpAssignee, FollowUpMade, MyTasks, Person, Queue, Severity, Task, TaskDepartments, TaskEvent, TimeSpent } from '../tasks/model.ts';

const json = (body: unknown): RequestInit => ({
  method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
});

// ---------------------------------------------------------------- reads

/** One person a task may be given to; in_department, so the department's own are listed before tiers 1-2 (A-#145 (c)). */
export type Assignee = Person & { role: string; in_department: boolean };

export function useMyTasks(enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'mine'],
    queryFn: () => apiFetch<MyTasks>('/tasks/mine', getToken),
    enabled,
    ...queryDefaults,
  });
}

/** Which departments the reader may use in each form (route 5a). The screens offer only these. */
export function useTaskDepartments() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'departments'],
    queryFn: () => apiFetch<TaskDepartments>('/tasks/departments', getToken),
    ...queryDefaults,
    staleTime: 5 * 60_000,
  });
}

export function useQueues(enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'queues'],
    queryFn: () => apiFetch<{ queues: Queue[] }>('/tasks/queues', getToken),
    enabled,
    ...queryDefaults,
  });
}

/** A department's unassigned work. The department is one of five fixed words, never typed text. */
export function useQueue(department: Department | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'queue', department],
    queryFn: () => apiFetch<{ department: Department; tasks: Task[] }>(
      `/tasks/queue?department=${encodeURIComponent(department!)}`, getToken),
    enabled: department !== null,
    ...queryDefaults,
  });
}

/**
 * A-#83 A5 (contract v4, route 5c): every open overdue task in a department, whoever holds it, for its owning director, the
 * Executive Case Reviewer and tiers 1-2. The department is one of five fixed words, never typed text.
 */
export function useDepartmentOverdue(department: Department | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'department-overdue', department],
    queryFn: () => apiFetch<{ department: Department; today: string; tasks: Task[] }>(
      `/tasks/department-overdue?department=${encodeURIComponent(department!)}`, getToken),
    enabled: department !== null,
    ...queryDefaults,
  });
}

export function useTask(id: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'one', id],
    queryFn: () => apiFetch<{ task: Task; events: TaskEvent[] }>(`/tasks/${id}`, getToken),
    enabled: id !== null,
    ...queryDefaults,
  });
}

/** 153: a completion's versions, describeVersions' shape (as /communications/:id/versions); read once it was edited. */
export interface CompletionVersion {
  version: number; kind: 'as recorded' | 'edit' | 'withdrawal'; reason: string | null; reason_label: string | null;
  reason_text: string | null; recorded_by: string | null; recorded_at: string;
  changes: { field: string; label: string; before: string | null; after: string | null }[];
}
export function useTaskVersions(id: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'versions', id],
    queryFn: () => apiFetch<{ versions: CompletionVersion[] }>(`/tasks/${id}/versions`, getToken),
    enabled,
    ...queryDefaults,
  });
}
/** The versions as the shared EarlierVersions component reads them (as communications, 138). */
export function asCompletionEdits(versions: CompletionVersion[]): EditLike[] {
  return versions.map((v) => ({ version: v.version, kind: v.kind, by: v.recorded_by, at: v.recorded_at,
    reason: v.reason_label ?? v.reason, reason_text: v.reason_text, changes: v.changes }));
}

/** 151: who a follow-up of this task may go to, the server's list (never /tasks/assignees for a follow-up of a task). */
export function useFollowUpAssignees(taskId: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'follow-up-assignees', taskId],
    queryFn: () => apiFetch<{ people: FollowUpAssignee[] }>(`/tasks/${taskId}/follow-up-assignees`, getToken),
    enabled: taskId !== null,
    ...queryDefaults,
  });
}

export function useAssignees(department: Department | null, purpose: 'assign' | 'follow_up') {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'assignees', department, purpose],
    queryFn: () => apiFetch<{ people: Assignee[]; queue_allowed: boolean }>(
      `/tasks/assignees?department=${encodeURIComponent(department!)}&purpose=${purpose}`, getToken),
    enabled: department !== null,
    ...queryDefaults,
  });
}

// ---------------------------------------------------------------- writes

/** Every write changes what some list shows, so every task list is asked again rather than patched. */
function useTaskWrite<I, O>(call: (input: I, getToken: () => Promise<string>) => Promise<O>) {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: I) => call(input, getToken),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['client'] });
      void qc.invalidateQueries({ queryKey: ['work'] });
    },
  });
}

/**
 * The card was opened (contract v3, route 7a): records "started" for this holding the first time, and NEVER changes the
 * status (A-#79: a glance never leaves a task In Progress). 200 for a non-holder or a closed task, so the card calls it
 * unconditionally. Only the card's own task is asked again: no list changes.
 */
export function useOpenedTask() {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ task: Task }>(`/tasks/${id}/opened`, getToken, json({})),
    onSuccess: (_r, id) => { void qc.invalidateQueries({ queryKey: ['tasks', 'one', id] }); },
  });
}

/** Start pressed (route 7b): the ONLY way a task becomes In Progress. Never called by opening the card. */
export const useStartTask = () =>
  useTaskWrite<string, { task: Task }>((id, t) => apiFetch(`/tasks/${id}/start`, t, json({})));

export interface CompleteInput { id: string; note: string; spent: TimeSpent; happened_at?: string; follow_up?: FollowUp }
export const useCompleteTask = () =>
  useTaskWrite<CompleteInput, { task: Task; follow_up: FollowUpMade | null }>(({ id, note, spent, happened_at, follow_up }, t) =>
    apiFetch(`/tasks/${id}/complete`, t, json({ note, ...spent, ...(happened_at ? { happened_at } : {}), ...(follow_up ? { follow_up } : {}) })));

/** 153: a correction of the completion, with a reason; only what changed is sent. */
export interface EditCompletionInput {
  id: string; reason: string; reason_text: string | null; note?: string; minutes?: number; happened_at?: string; what?: string;
}
export const useEditCompletion = () =>
  useTaskWrite<EditCompletionInput, { version: number; task: Task }>(({ id, ...body }, t) =>
    apiFetch(`/tasks/${id}/completion/edit`, t, json(body)));

export interface ActionInput {
  client_id: string; department?: Department; what: string; note: string; spent: TimeSpent;
  happened_at?: string; task_id?: string; follow_up?: FollowUp;
}
export const useRecordAction = () =>
  useTaskWrite<ActionInput, { action: Task; follow_up: FollowUpMade | null }>(({ spent, ...rest }, t) =>
    apiFetch('/tasks/actions', t, json({ ...rest, ...spent })));

export interface NewTaskInput {
  client_id: string | null; department: Department; title: string; detail?: string;
  assigned_to_user_id: string | null; due: DueSpec; severity?: Severity; follow_up_of?: string;
}
export const useCreateTask = () =>
  useTaskWrite<NewTaskInput, { task: Task }>((input, t) => apiFetch('/tasks', t, json(input)));

export const useReassignTask = () =>
  useTaskWrite<{ id: string; assigned_to_user_id: string | null; reason?: string }, { task: Task }>(({ id, ...body }, t) =>
    apiFetch(`/tasks/${id}/reassign`, t, json(body)));

export const usePriority = () =>
  useTaskWrite<{ id: string; severity: Severity }, { task: Task }>(({ id, severity }, t) =>
    apiFetch(`/tasks/${id}/priority`, t, json({ severity })));

export const useCloseTask = () =>
  useTaskWrite<{ id: string; how: 'waive' | 'cancel'; reason: string }, { task: Task }>(({ id, how, reason }, t) =>
    apiFetch(`/tasks/${id}/${how}`, t, json({ reason })));

/** Ruling 17's "seen", one batch per render: see useSeen() in tasks/seen.ts. Never invalidates: nothing shown changes. */
export function postSeen(getToken: () => Promise<string>, taskIds: string[]) {
  return apiFetch<{ recorded: number }>('/tasks/seen', getToken, json({ task_ids: taskIds }));
}
